import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import DosageCalculator from 'pages/DosageCalculator';
import OusiaDosageCalculator from 'pages/OusiaDosageCalculator';
import SemaglutideCalculator from 'pages/SemaglutideCalculator';
import FAQList from 'pages/FAQList';
import FAQPage from 'pages/FAQPage';
import Header from 'components/Header';
import Footer from 'components/Footer';
import Terms from 'pages/Terms';
import PrivacyPolicy from 'pages/Privacy';
import About from 'pages/About';
import FindingConcentration from 'pages/guides/FindingConcentration';
import QuizConcentrationLabel from 'pages/QuizConcentrationLabel';
import 'styles/main.scss';
import Analytics from './services/analytics';
import ReverseDoseCalc from 'pages/ReverseDoseCalc';
import ZepboundReverseCalc from 'pages/ZepboundReverseCalc';
import VialTransitionCalculator from 'pages/VialTransitionCalculator';
import SplitDoseCalculator from 'pages/SplitDoseCalculator';
import Cookies from 'js-cookie';
import AnalyticsConsent from 'components/AnalyticsConsent';
import { HelmetProvider } from 'react-helmet-async';
import NotFoundPage from './pages/NotFoundPage';
import Home from 'pages/Home';
import { isCrawler } from 'utils/crawlerDetection';

const DisclaimerModal = lazy(() => import('./components/DisclaimerModal'));

// Handle both analytics and scroll behavior on route changes
const PageViewTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Track pageview and scroll to top on route changes
    Analytics.trackPageView(location.pathname + location.search);
    window.scrollTo(0, 0);
  }, [location]);

  return null;
};

const App: React.FC = () => {
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  useEffect(() => {
    const hasAcceptedDisclaimer = Cookies.get('disclaimerAccepted');
    // Don't show disclaimer for web crawlers (improves SEO and performance scores)
    if (!hasAcceptedDisclaimer && !isCrawler()) {
      setShowDisclaimer(true);
    }
  }, []);

  return (
    <HelmetProvider>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true,
        }}
      >
        {showDisclaimer && (
          <Suspense fallback={null}>
            <DisclaimerModal onClose={() => setShowDisclaimer(false)} />
          </Suspense>
        )}
        <Header />
        <div className="content-area">
          <PageViewTracker />
          <Routes>
            {/* <Route path="/" element={<Home />} /> */}
            <Route path="/" element={<DosageCalculator />} />
            <Route path="/dosage-calculator" element={<DosageCalculator />} />
            <Route path="/semaglutide-calculator" element={<SemaglutideCalculator />} />
            <Route path="/ousia-dosage-calculator" element={<OusiaDosageCalculator />} />
            <Route path="/reverse-dosage-calculator" element={<ReverseDoseCalc />} />
            <Route path="/zepbound-reverse-calculator" element={<ZepboundReverseCalc />} />
            <Route path="/vial-transition-calculator" element={<VialTransitionCalculator />} />
            <Route path="/split-dose-calculator" element={<SplitDoseCalculator />} />
            <Route path="/faq/:faqId" element={<FAQPage />} />
            <Route path="/faq" element={<FAQList />} />
            <Route path="/faq/finding-concentration" element={<FindingConcentration />} />
            <Route path="/quiz/concentration-label" element={<QuizConcentrationLabel />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/about" element={<About />} />
            <Route path="/404" element={<NotFoundPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </div>
        <Footer />
        {/* <AnalyticsConsent /> */}
      </Router>
    </HelmetProvider>
  );
};

export default App;
